import {
  Field,
  Filter,
  RangePicker,
  Text,
  TimePicker,
  MultipleSelect,
  Select,
  Toast,
} from "components/commons";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import lang from "translations";
import { useApi, useMount, useFilter } from "hooks";
import { dashboardFilterState } from "./filters";
import { modifyFromTimeRange, modifyToTimeRange } from "services";
import { searchLocationWithTransaction } from "apis";
import { measureTime } from "services/date.service";
import { prettifyIntervalTime } from "services/pretty.service";

const DashboardFilter = ({ venue, venueId, fetchCharts }) => {
  const { modifyFilters, modifyFilter, clearFilter, filterState } = useFilter(
    dashboardFilterState(venue)
  );
  const [warningDateRange, setWarningDateRange] = useState(false);

  const {
    request: searchLocationRequest,
    loading,
    mappedData: locationOptions,
    error,
  } = useApi({
    api: searchLocationWithTransaction,
    params: {
      venueId,
    },
    mapper: {
      value: {
        key: "locationId",
      },
      text: {
        key: "locationName",
      },
    },
    isArray: true,
    handleOwnError: true,
  });

  const checkWarningDateRange = useCallback(([to, end]) => {
    let diff = to.diff(end, "day");
    diff = Math.abs(diff);
    return diff > 365;
  }, []);

  const intervalOptions = useMemo(() => {
    const dateRange = filterState.dateRange;
    let ret = measureTime([dateRange[0], dateRange[1]]).getOptType();
    ret = ret.map((i) => ({
      value: i,
      text: prettifyIntervalTime(i),
    }));

    return ret;
  }, [filterState.dateRange]);

  useEffect(() => {
    modifyFilter("interval", { ...intervalOptions[0] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [intervalOptions]);

  const submit = useCallback(
    async (filter) => {
      await fetchCharts({
        ...filter,
        locationIds: filter.locationIds.map((l) => {
          return l.value;
        }),
      });
    },
    [fetchCharts]
  );

  const applyFilterCb = useCallback(async () => {
    const { requestState } = modifyFilters({ page: 1 });
    if (warningDateRange) {
      Toast({
        content: lang.onlyDateRangesLessThanOneYear,
        error: true,
        icon: "exclamation-fill",
      }).open();
      return;
    }
    await submit(requestState);
  }, [modifyFilters, submit, warningDateRange]);

  const clearFilterCb = useCallback(() => {
    const { requestState } = clearFilter();
    submit(requestState);
  }, [clearFilter, submit]);

  useMount(() => {
    searchLocationRequest({
      venueId,
      page: 1,
      pageSize: 100,
      //startDateTime: toApiDateTimeFormat(dateRange[0]),
      //endDateTime: toApiDateTimeFormat(dateRange[1]),
    });
    submit(filterState);
  });

  //eslint-disable-next-line
  // useEffect(
  //   debounced(1000, () => {
  //     const dateRange = filterState.dateRange;
  //     searchLocationRequest({
  //       venueId,
  //       startDateTime: toApiDateTimeFormat(dateRange[0]),
  //       endDateTime: toApiDateTimeFormat(dateRange[1], true),
  //     });
  //   }),
  //   [filterState.dateRange]
  // );

  useEffect(() => {
    const check = checkWarningDateRange(filterState.dateRange);
    setWarningDateRange(check);
    if (check) {
      Toast({
        content: lang.onlyDateRangesLessThanOneYear,
        error: true,
        icon: "exclamation-fill",
      }).open();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterState.dateRange]);

  return (
    <Filter
      className="my-lg"
      onClear={clearFilterCb}
      onApply={applyFilterCb}
      searchBox={false}
      filterState={filterState}
      actionsSpan={3}
    >
      <Field className="col-span-3" filterLabel={lang.dateRange}>
        <RangePicker
          value={filterState.dateRange}
          name="dateRange"
          onChange={(name, value) => {
            if (value) {
              modifyFilter(name, {
                value,
              });
            }
          }}
        />
      </Field>
      <Field className="col-span-3" filterLabel={lang.timeRange}>
        <div className="flex items-center">
          <TimePicker
            value={filterState.dateRange[0]}
            onChange={(val) => {
              modifyFromTimeRange(val, filterState, modifyFilter);
            }}
          />
          <Text className="mx-sm" color="text-black-light">
            {lang.to}
          </Text>
          <TimePicker
            value={filterState.dateRange[1]}
            onChange={(val) => {
              modifyToTimeRange(val, filterState, modifyFilter);
            }}
          />
        </div>
      </Field>
      <Field className="col-span-3" filterLabel={lang.interval}>
        <Select
          name="interval"
          options={intervalOptions}
          value={filterState.interval}
          onChange={modifyFilter}
          sortOptions={false}
        />
      </Field>
      <Field className="col-span-3" filterLabel={lang.outlet}>
        <MultipleSelect
          name="location"
          error={error}
          disabled={error || !locationOptions.length}
          emptyPlaceHolder={lang.noLocationAvailable}
          selectAllText={lang.allLocations}
          loading={loading}
          options={locationOptions}
          value={filterState.locationIds}
          onChange={(name, { value }) => {
            modifyFilters({
              locationIds: value,
            });
          }}
          placeholder={lang.selectLocation}
        />
      </Field>

      <div className="col-span-9"></div>
    </Filter>
  );
};

export default DashboardFilter;
